// Import statements for Ant Design icons used in the UI
import { CheckCircleOutlined, ExclamationCircleOutlined, ExportOutlined, FieldTimeOutlined, FileImageOutlined, InfoCircleOutlined, IssuesCloseOutlined, MailOutlined, SaveOutlined, ShopOutlined, ToolOutlined, UploadOutlined } from "@ant-design/icons";

// Import Ant Design components for UI elements
import { Button, Card, Input, InputNumber, message, Radio, Select, Switch, Upload } from "antd";

// Import custom components
import ReusableButton from "components/Elements/ReusableButton";

// Import API constants and storage constants
import { API_FULL_URL } from "constants/ApiConstant";
import { AUTH_TOKEN, FIRMSTAMPCOUNT, ISFEEDBACKENABLED, ISINFORMATIONSELECT, ISMULTIIPLESTAMPENABLED, ISSTAMTPRODUCTSELECT, LANGUAGE } from "constants/StorageConstant";

// Import network utility for API calls
import network from "network/network";

// Import React hooks and internationalization
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Destructure TextArea from Input component
const { TextArea } = Input;

/**
 * FirmSettings Component
 * 
 * This component manages all firm-specific settings including:
 * - Stamp count and time limits
 * - Product selection settings
 * - Email requirements
 * - Menu configuration
 * - Chatbot settings
 * - Feedback and multiple stamp options
 * 
 * @param {Function} changeTab - Function to change active tab in parent component
 */
const FirmSettings = ({ changeTab }) => {
    // Translation hook for multi-language support
    const { t, i18n } = useTranslation();
    
    // Main state object containing all firm settings
    const [firmSettings, setfirmSettings] = useState({
        stampTimeLimit: 0,              // Time limit between stamps (in minutes)
        getMailStampOrder: 0,           // When to request email from customer (0 = registration, 1+ = stamp number)
        isStampProductSelect: false,    // Whether customers can select products for stamps
        isInformationSelect: false,     // Whether to show information messages after stamping
        stampCount: 0,                  // Number of stamps required for gift
        isMailRequired: false,          // Whether email is mandatory for customers
        isMenuSelect: false,            // Whether menu feature is enabled
        menuType: 0,                    // Menu type (1 = PDF, 2 = Link)
        menuPath: "",                   // Path to menu file or URL
        isFeedbackEnabled: false,       // Whether feedback feature is enabled
        isMultipleStampEnabled: false,  // Whether multiple stamps can be given at once
        mainProductName: "",            // Name of the main product for stamps
        giftProductName: "",            // Name of the gift product
        botText: "",                    // Welcome message for chatbot
        customSectionVisible: false,    // Whether custom chatbot section is visible
        botUrl: "",                     // Website URL for chatbot
        botPhoneNumber: "",             // Phone number for chatbot
        botMediaUrl: "",                // Media URL for chatbot image
        stampExpireHour: 0              // Hours after which stamps expire
    });
    // Additional state variables
    const [isExistRecord, setIsExistRecord] = useState(false);  // Whether settings record exists in database
    const [goInformation, setGoInformation] = useState(localStorage.getItem(ISINFORMATIONSELECT));  // Information select status from localStorage
    const [menuType, setMenuType] = useState();  // Current menu type selection
    const [file, setFile] = useState();  // Currently selected file for upload
    
    // State for custom chatbot section configuration
    const [customSection, setCustomSection] = useState({
        showImage: false,      // Whether to show image in chatbot
        showText: false,       // Whether to show text in chatbot
        showButton1: false,    // Whether to show first button
        showButton2: false,    // Whether to show second button
        showButton3: false,    // Whether to show third button
        textValue: '',         // Text content for chatbot
        imageFile: null,       // Selected image file for chatbot
        phoneNumber: '',       // Phone number for contact button
        website: ''            // Website URL for contact button
    });

    // Available options for chatbot quick buttons
    // Commented out static options - now loaded dynamically from API
    // const customOptions = [
    //     { key: '1', label: "Damga İsteği" },
    //     { key: '2', label: "Menü Görüntüle" },
    //     { key: '3', label: "Görüş ve Öneri" },
    // ];

    // State for chatbot button options and selections
    const [customOptions, setCustomOptions] = useState([]);     // Available button options
    const [selectedOptions, setSelectedOptions] = useState([]); // Currently selected button options

    // State to control visibility of custom chatbot section
    const [customSectionVisible, setCustomSectionVisible] = useState(true);

    // Error state variables for form validation
    const [menuError, setMenuError] = useState("");        // Menu configuration errors
    const [botTextError, setBotTextError] = useState("");  // Chatbot text validation errors
    // App selection flag (hide certain fields for chatshireQr)
    const [isQrApp, setIsQrApp] = useState(false);

    // Robust detector for QR app across different storage sources
    const determineIsQrApp = () => {
        try {
            const selectedApp = JSON.parse(localStorage.getItem('selectedApp') || "{}");
            const leftMenuList = JSON.parse(localStorage.getItem('LEFT_MENU_LIST') || "[]");
            const appName = (selectedApp?.appName || leftMenuList?.[0]?.appName || "").toString().toLowerCase();
            return appName === "chatshireqr";
        } catch (e) {
            return false;
        }
    }

    /**
     * Handles selection/deselection of chatbot button options
     * Maximum 3 options can be selected at once
     * 
     * @param {number} key - Unique key of the option
     * @param {string} label - Display label of the option
     */
    const handleSelectOption = (key, label) => {
        const exists = selectedOptions.find(opt => opt.key === key);
        if (exists) {
            // Remove option if already selected
            setSelectedOptions(selectedOptions.filter(opt => opt.key !== key));
          
        } else if (selectedOptions.length < 3) {
            // Add option if under limit
            setSelectedOptions([...selectedOptions, { key, label }]);
            // If "Menu Display" option is selected, enable menu selection
            if (key === 2) {
                setfirmSettings(prev => ({ ...prev, isMenuSelect: true }));
            }
        }
    };

    /**
     * useEffect hook to initialize component
     * - Loads settings from API
     * - Sets up localStorage change listener
     */
    useEffect(() => {
        getSettings();

        // Handler for localStorage changes (cross-tab synchronization)
        const handleStoreChange = () => {
            setGoInformation(localStorage.getItem(ISINFORMATIONSELECT));
            setIsQrApp(determineIsQrApp());
        };

        // Listen for storage events to sync across browser tabs
        window.addEventListener("storage", handleStoreChange);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener("storage", handleStoreChange);
        };
    }, [])

    // Detect selected app initially
    useEffect(() => {
        setIsQrApp(determineIsQrApp());
    }, [])

    // Placeholder for future firmSettings effect
    // useEffect(()=>{
    // },[firmSettings])

    /**
     * Fetches current firm settings and chatbot button configurations from API
     * Updates component state with retrieved data
     */
    const getSettings = async () => {
        // Determine app type locally to avoid race with state on initial load
        const isQr = determineIsQrApp();
        // Fetch firm settings from API
        const resultSettings = await network.get("/admin/CoffeeSettings/GetSettings", {}, false);

        if (resultSettings?.data?.data) {
            // Extract settings data from API response
            const { data } = resultSettings.data;
            // Map API response to component state
            setfirmSettings({
                stampTimeLimit: data.stamptimelimit,                    // Time limit between stamps
                getMailStampOrder: data.getmailstamporder,             // When to request email
                isInformationSelect: data.isinformationselect,         // Show info messages
                stampCount: data.stampcount,                           // Required stamps for gift
                isMailRequired: data.ismailrequired,                   // Email mandatory flag
                isStampProductSelect: data.isstampproductselect,       // Product selection enabled
                firmSettingsId: data.firmsettingsid,                   // Unique settings ID
                isMenuSelect: data.ismenuenabled,                      // Menu feature enabled
                menuType: data.menutype,                               // Menu type (PDF/Link)
                menuPath: data.menupath,                               // Menu file path or URL
                isFeedbackEnabled: data.isfeedbackenabled,             // Feedback feature enabled
                isMultipleStampEnabled: data.ismultiplestampenabled,   // Multiple stamps allowed
                mainProductName: data.mainproductname,                 // Main product name
                botText: data.bottext,                                 // Chatbot welcome text
                botPhoneNumber: data.botphonenumber,                   // Chatbot phone number
                botUrl: data.boturl,                                   // Chatbot website URL
                botMediaUrl: data.botmediapath,                        // Chatbot image URL
                stampExpireHour: data.stampexpirehour || 0,            // Stamp expiration hours
                giftProductName: data.giftproductname                  // Gift product name
            })
            // Mark that settings record exists in database
            setIsExistRecord(true);
            setMenuType(data.menutype);

            // Update custom chatbot section with existing data
            setCustomSection(cs => ({ 
                ...cs, 
                textValue: data.bottext,           // Set existing bot text
                website: data.boturl,              // Set existing website URL
                phoneNumber: data.botphonenumber   // Set existing phone number
            }));
            // Set chatbot section visibility based on API data
            setCustomSectionVisible(data.ischatbotenabled);
        }

        // Fetch chatbot button configurations from API
        const botButtons = await network.get("/admin/CoffeeSettings/GetButtons", {}, false)


        // Define default button options with translations
        const defaultOptions = [
            { key: 1, label: t("stampRequest") },        // Stamp request button
            { key: 2, label: t("menuRequest") },         // Menu display button
            { key: 3, label: t("feedbackRequest") },     // Feedback button
            { key: 4, label: t("phoneRequest") },        // Phone contact button
            { key: 5, label: t("websiteRequest") },      // Website button
            // { key: 6, label: t("customerServiceRequest") }  // Customer service (disabled)
        ];
        // For chatshireQr, add extra quick button: "Varsa Kart Bilgisi"
        if (isQr) {
            defaultOptions.push({ key: 6, label: "Kart Bilgisi" });
        }
        // If current app is chatshireQr, remove stamp request from defaults
        const filteredDefaultOptions = isQr
            ? defaultOptions.filter(opt => opt.key !== 1)
            : defaultOptions;

        if (botButtons?.isSuccess && Array.isArray(botButtons.data.data) && botButtons.data.data.length > 0) {
            // Process buttons retrieved from API
            let options = botButtons.data.data.map((item, index) => ({
                key: index + 1,
                label: item.text,
                status: item.status
            }));

            // If QR app, ensure stamp request is not present even if returned by API
            if (isQr) {
                options = options.filter(opt => opt.label !== t("stampRequest"));
            }

            // Add any missing default buttons that aren't in API response
            filteredDefaultOptions.forEach(def => {
                if (!options.some(opt => opt.label === def.label)) {
                    options.push({ ...def, status: false });
                }
            });

            setCustomOptions(options);

            // Filter and set only the active/selected buttons
            var keys = options.filter(i => i.status === true);
            setSelectedOptions(keys);
        } else {
            // Fallback to default options if API call fails
            setCustomOptions(filteredDefaultOptions);
        }

    }

    /**
     * Uploads chatbot media image to server
     * 
     * @param {File} file - Image file to upload
     * @returns {Promise<Response>} - Fetch response object
     */
    const uploadImage = async (file) => {
        if (!file) return null;
        
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('firmsettingsId', firmSettings.firmSettingsId); // Associate with current firm

        // Upload file using fetch API with authorization
        const response = await fetch(API_FULL_URL + '/admin/CoffeeSettings/uploadBotMedia', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
            },
            body: formData
        });

        return response;
    };

    /**
     * Handles saving or updating firm settings
     * Performs comprehensive validation before submitting data
     */
    const handleSaveOrUpdate = async () => {
        let hasError = false;
        // Clear previous error messages
        setMenuError("");
        setBotTextError("");
        // Validation: Chatbot welcome message is required when chatbot is enabled
        if (customSectionVisible && (!customSection?.textValue?.trim())) {
            const msg = t('welcomeBotValidationError') || 'Karşılama botu: Lütfen en az bir buton seçin ve karşılama mesajı girin.';
            setBotTextError(msg);
            message.error(msg);
            hasError = true;
        }

        // Validation: Website URL is required when website button is selected
        if (customSectionVisible && selectedOptions.some(option => option.key === 5) && (!customSection?.website?.trim())) {
            message.error(t('welcomeBotValidationError') || 'Karşılama botu: Web sitesi alanı boş olamaz.');
            return;
        }

        // Validation: Phone number is required when phone button is selected
        if (customSectionVisible && selectedOptions.some(option => option.key === 4) && (!customSection?.phoneNumber?.trim())) {
            message.error(t('welcomeBotValidationError') || 'Karşılama botu: Telefon numarası alanı boş olamaz.');
            return;
        }

        // Validation: Image is required when chatbot is enabled
        if (customSectionVisible && (!customSection.imagePreview && !firmSettings.botMediaUrl)) {
            message.error(t('welcomeBotValidationError') || 'Karşılama botu: Görsel yüklenmelidir.');
            return;
        }

        // Validation: Product names are mandatory
        if (!firmSettings.mainProductName || !firmSettings.giftProductName) {
            message.error('Ürün isimleri: Ürün isimleri boş bırakılamaz.');
            return;
        }

        // Validation: Menu configuration when menu is enabled
        if (firmSettings.isMenuSelect === true) {
            if (!firmSettings.menuType || firmSettings.menuType === 1) {
                // PDF menu validation
                if (!firmSettings.menuPath || firmSettings.menuPath === "") {
                    const msg = t("errorMenuPath") || "Menü (PDF): PDF menü dosyası yüklenmelidir.";
                    setMenuError(msg);
                    message.error(msg);
                    hasError = true;
                }
            } else if (firmSettings.menuType === 2) {
                // Link menu validation
                if (!firmSettings.menuPath || firmSettings.menuPath.trim() === "") {
                    const msg = t("errorMenuPath") || "Menü (Link): Menü linki girilmelidir.";
                    setMenuError(msg);
                    message.error(msg);
                    hasError = true;
                }
            }
        }

        // Duplicate validation check (can be removed)
        if (!firmSettings.mainProductName || !firmSettings.giftProductName) {
            message.error('Ürün isimleri: Ürün isimleri boş bırakılamaz.');
            return;
        }

        // Stop execution if any validation errors occurred
        if (hasError) return;

        // Check if feedback button is selected to enable feedback feature
        let isFeedbackSelected = selectedOptions.some(option => option.key === 3 || option.label === t('feedbackRequest'));

        // Prepare data model for API submission
        var model = {
            stampTimeLimit: firmSettings.stampTimeLimit,                // Time limit between stamps
            getMailStampOrder: firmSettings.getMailStampOrder,         // When to request email
            isInformationSelect: firmSettings.isInformationSelect,     // Show information messages
            stampCount: firmSettings.stampCount,                       // Required stamps for gift
            isMailRequired: firmSettings.isMailRequired,               // Email mandatory flag
            isStampProductSelect: firmSettings.isStampProductSelect,   // Product selection enabled
            firmSettingsId: firmSettings.firmSettingsId,               // Unique settings ID
            isMenuSelect: firmSettings.isMenuSelect,                   // Menu feature enabled
            menuType: !firmSettings.menuType ? menuType : firmSettings.menuType,  // Menu type with fallback
            menuPath: firmSettings.menuPath ?? file.name,              // Menu path with fallback to filename
            isFeedbackEnabled: firmSettings.isFeedbackEnabled,         // Feedback feature enabled
            isMultipleStampEnabled: firmSettings.isMultipleStampEnabled, // Multiple stamps allowed
            mainProductName: firmSettings.mainProductName,             // Main product name
            giftProductName: firmSettings.giftProductName,             // Gift product name
            botText: customSection.textValue,                          // Chatbot welcome text
            isChatBotEnabled: customSectionVisible,                    // Chatbot enabled flag
            botUrl: customSection.website,                             // Chatbot website URL
            botPhoneNumber: customSection.phoneNumber,                 // Chatbot phone number
            stampExpireHour: firmSettings.stampExpireHour              // Stamp expiration hours
        }

        
        // Update existing settings or create new ones
        if (isExistRecord) {
            // Update existing settings
            const result = await network.post("/admin/CoffeeSettings/UpdateSettings", model, false);

            if (result.isSuccess) {
                message.success(t("settingsSaved"));
                
                // Update localStorage with new values for cross-component synchronization
                localStorage.setItem(FIRMSTAMPCOUNT, model.stampCount);
                localStorage.setItem(ISSTAMTPRODUCTSELECT, model.isStampProductSelect);
                localStorage.setItem(ISINFORMATIONSELECT, model.isInformationSelect);
                localStorage.setItem(ISMULTIIPLESTAMPENABLED, model.isMultipleStampEnabled);
                localStorage.setItem(ISFEEDBACKENABLED, isFeedbackSelected ? true : firmSettings.isFeedbackEnabled)
                
                // Trigger storage event for cross-tab synchronization
                window.dispatchEvent(new Event("storage"));


                // Count selected chatbot button options
                const selectedCount = customOptions.filter(opt => !!selectedOptions.find(sel => sel.key === opt.key)).length;

                // Prepare chatbot button data with appropriate types based on selection
                const dataToSend = customOptions.map(opt => {
                    const selected = !!selectedOptions.find(sel => sel.key === opt.key);
                    
                    if (selectedCount === 1 && opt.key === 5 && selected) {
                        // Single website button - use CTA URL type
                        return {
                            text: opt.label,
                            status: true,
                            type: 'cta_url',
                        };
                    } else if (selectedCount === 1 && opt.key === 4 && selected) {
                        // Single phone button - use phone URL type
                        return {
                            text: opt.label,
                            status: true,
                            type: 'phone_url',
                        };
                    } else {
                        // All other cases - use quick reply type
                        return {
                            text: opt.label,
                            status: selected,
                            type: 'quick_reply'
                        };
                    }
                });

                // Update chatbot button configurations
                const resultButton = await network.post("/admin/CoffeeSettings/UpdateOrAddButton", dataToSend, false);

            } else {
                message.error(t("settingsSaveError"));
            }
        } else {
            // Create new settings record
            const result = await network.post("/admin/CoffeeSettings/AddSettings", model, false);

            if (result.isSuccess) {
                message.success(t("settingsSaved"));
            } else {
                message.error(t("settingsSaveError"));
            }
        }

        // Upload chatbot image if a new file was selected
        let uploadedImageResult = null;
        if (customSection.imageFile) {
            uploadedImageResult = await uploadImage(customSection.imageFile);
            
            // Refresh settings to get updated image URL
            getSettings();
        }
    }


    /**
     * Configuration object for PDF menu file upload
     * Returns Ant Design Upload component props
     */
    const handleUpload = () => ({
        name: 'file',
        action: API_FULL_URL + `/admin/CoffeeSettings/ReplacePdfFile`,
        method: 'POST',
        headers: {
            authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
        },
        showUploadList: false,  // Don't show file list
        onChange(info) {
            if (info.file.status === 'done') {
                message.success("Başarılı şekilde değişti");
                
                // Update settings with new file information
                var model = {
                    menuType: firmSettings.menuType,
                    menuPath: info.file.name,
                    firmSettingsId: firmSettings.firmSettingsId,
                    isMenuSelect: firmSettings.isMenuSelect,
                }

                // Update component state with uploaded file info
                setfirmSettings({ ...firmSettings, menuPath: info.file.name, menuType: 1 })
                setMenuType(1);

            } else if (info.file.status === 'error') {
                message.error("Hata");
            }
        }
    });

    /**
     * Validates file before upload for chatbot image
     * Checks file type, name format, and size
     * 
     * @param {File} file - File to be validated
     * @returns {boolean|string} - true if valid, Upload.LIST_IGNORE if invalid
     */
    const handleBeforeUpload = (file) => {
        // Check if file is a valid image type
        const isImage = file.type === 'image/jpeg' || file.type === 'image/png';

        // Validate filename contains only allowed characters
        const isValidFileName = /^[a-zA-Z0-9._-]+$/.test(file.name);

        // Check image size is under 5MB limit
        const isValidImageSize = file.size / 1024 / 1024 < 5;

        // Validate filename format
        if (!isValidFileName) {
            message.error(t('invalidFileName'));
            return Upload.LIST_IGNORE;
        }
        
        // Validate image size
        if (isImage && !isValidImageSize) {
            message.error(t('imageSizeError'));
            return Upload.LIST_IGNORE;
        } else {
            // Update custom section with selected file and preview
            setCustomSection(cs => ({
                ...cs,
                imageFile: file,
                imagePreview: URL.createObjectURL(file)  // Create preview URL
            }));
        }

        return true;
    };


    // Render the firm settings form
    return (
        <div>
            <Card>

                {/* Gift Stamp Count Setting */}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <h3><CheckCircleOutlined /> {t("giftStampCount")}</h3>
                        <p>{t("giftStampCountDescription")}</p>
                    </div>
                    <div className="mt-3" style={{ width: "100px" }}>
                        <Select
                            style={{ width: "100%" }}
                            value={firmSettings.stampCount}
                            onChange={(value) => setfirmSettings({ ...firmSettings, stampCount: value })}
                            options={[
                                { value: 5, label: "5" },
                                { value: 6, label: "6" },
                                { value: 7, label: "7" },
                                { value: 8, label: "8" },
                                { value: 9, label: "9" },
                                { value: 10, label: "10" },
                                { value: 11, label: "11" },
                                { value: 12, label: "12" },
                                { value: 13, label: "13" },
                                { value: 14, label: "14" },
                                { value: 15, label: "15" }
                            ]}
                        />
                    </div>
                </div>
                <hr style={{ opacity: "50%" }} />

                {/* Product Selection Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><ShopOutlined /> {t("productSelection")}</h3>
                                <p>{t("productSelectionDescription")}</p>
                            </div>
                            <div className="mt-3">
                                <Switch checked={firmSettings.isStampProductSelect === true} size="default" onChange={(value) => setfirmSettings({ ...firmSettings, isStampProductSelect: value })} />
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}

                {/* Email Request Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><MailOutlined /> {t("emailRequestFromCustomer")}</h3>
                                <p>{t("emailRequestDescription")}</p>
                            </div>
                            <div className="mt-3">
                                <Select
                                    value={firmSettings.getMailStampOrder}
                                    style={{ width: "150px" }}
                                    onChange={(value) => setfirmSettings({ ...firmSettings, getMailStampOrder: value })}
                                    options={[
                                        { value: 0, label: t("duringRegistration") },
                                        ...Array.from({ length: firmSettings.stampCount }, (_, index) => ({ value: index + 1, label: `${index + 1}. ${t("Stamp")}` }))
                                    ]}
                                />
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}

                {/* Email Required Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><ExclamationCircleOutlined /> {t("emailRequired")}</h3>
                                <p>{t("emailRequiredDescription")}</p>
                            </div>
                            <div className="mt-3">
                                <Switch checked={firmSettings.isMailRequired === true} size="default" onChange={(value) => setfirmSettings({ ...firmSettings, isMailRequired: value })} />
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}

                {/* Post Stamp Message Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><InfoCircleOutlined /> {t("postStampMessage")}</h3>
                                <p>{t("postStampMessageDescription")}</p>
                            </div>
                            <div className="mt-3">
                                <div className="ml-auto" style={{ width: "fit-content" }} >
                                    <Switch checked={firmSettings.isInformationSelect} size="default" onChange={(value) => setfirmSettings({ ...firmSettings, isInformationSelect: value })} />
                                </div>
                                {goInformation === "true" ? (
                                    <div className="mt-2 mb-1">
                                        <Button type="default" onClick={() => changeTab("2")} icon={<ExportOutlined />}>{t("infoMessages")}</Button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}



                {/* Stamp Time Limit Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><FieldTimeOutlined /> {t("stampTimeLimit")}</h3>
                                <p>{t("stampTimeLimitDescription")}</p>
                            </div>
                            <div className="mt-3" style={{ width: "100px" }}>
                                <Select
                                    style={{ width: "100%" }}
                                    value={firmSettings.stampTimeLimit}
                                    onChange={(value) => setfirmSettings({ ...firmSettings, stampTimeLimit: value })}
                                    options={[
                                        { value: 0, label: "0" },
                                        { value: 15, label: "15" },
                                        { value: 30, label: "30" },
                                        { value: 60, label: "60" },
                                        { value: 120, label: "120" },
                                        { value: 480, label: "480" },
                                        { value: 1440, label: "1 Gün" }

                                    ]}
                                />
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}





                {/* Customize Chat Images Setting */}
                <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <h3><FileImageOutlined /> {t("customizeChatImages")}</h3>
                        <p>{t("customizeChatImagesDescription")}</p>
                    </div>
                    <div className="mt-3">
                        <div className="mt-2 mb-1">
                            <Button type="default" onClick={() => changeTab("3")} icon={<ExportOutlined />}>{t("chatMedia")}</Button>
                        </div>
                    </div>
                </div>

                <hr style={{ opacity: "50%" }} />



                {/* Feedback Setting */}
                <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <h3><IssuesCloseOutlined /> {t("feedback")}</h3>
                        <p>{t("feedbackEx")}</p>
                    </div>
                    <div className="mt-3">
                        <Switch checked={firmSettings.isFeedbackEnabled === true} size="default" onChange={(value) => setfirmSettings({ ...firmSettings, isFeedbackEnabled: value })} />
                    </div>
                </div>
                <hr style={{ opacity: "50%" }} />

                {/* Multiple Stamp Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><ExclamationCircleOutlined /> {t("multipleStamp")}</h3>
                                <p>{t("multipleStampEx")}</p>
                            </div>
                            <div className="mt-3">
                                <Switch checked={firmSettings.isMultipleStampEnabled === true} size="default" onChange={(value) => setfirmSettings({ ...firmSettings, isMultipleStampEnabled: value })} />
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}

                {/* Product Keyword Setting */}
                <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <h3><ExclamationCircleOutlined /> {t("productKeyword")}</h3>
                        <p>{t("productNameEx")}</p>
                    </div>
                    <div className="mt-3">
                        <Input placeholder={t("productKeyword")} value={firmSettings.mainProductName} onChange={(e) => setfirmSettings({ ...firmSettings, mainProductName: e.target.value })} />
                    </div>
                </div>
                <hr style={{ opacity: "50%" }} />

                {/* Gift Keyword Setting */}
                <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <h3><ExclamationCircleOutlined /> {t("giftKeyword")}</h3>
                        <p>{t("giftNameEx")}</p>
                    </div>
                    <div className="mt-3">
                        <Input placeholder={t("giftKeyword")} value={firmSettings.giftProductName} onChange={(e) => setfirmSettings({ ...firmSettings, giftProductName: e.target.value })} />
                    </div>
                </div>
                <hr style={{ opacity: "50%" }} />

                {/* Stamp Expiration Time Setting (hidden for chatshireQr) */}
                {!isQrApp && (
                    <>
                        <div className="mt-3" style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <h3><FieldTimeOutlined /> {t("stampExpireMinute") || "Stamp Expire Minute"}</h3>
                                <p>{t("stampExpireMinuteDescription") || "Select the expiration time (in minutes) for a stamp."}</p>
                            </div>
                            <div className="mt-3" style={{ width: "150px" }}>
                                <Select
                                    style={{ width: "100%" }}
                                    value={firmSettings.stampExpireHour}
                                    onChange={(value) => setfirmSettings({ ...firmSettings, stampExpireHour: value })}
                                    options={[
                                        { value: null, label: "Yok" },
                                        { value: 1, label: "1 Saat" },
                                        { value: 3, label: "3 Saat" },
                                        { value: 5, label: "5 Saat" },
                                        { value: 8, label: "8 Saat" },
                                        { value: 12, label: "12 Saat" },
                                        { value: 24, label: "24 Saat" },
                                        { value: 48, label: "48 Saat" },
                                        { value: 73, label: "72 Saat" }
                                    ]}
                                />
                            </div>
                        </div>
                        <hr style={{ opacity: "50%" }} />
                    </>
                )}


                {/* Custom Chatbot Section */}
                <div className="mt-4">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <h3><ToolOutlined /> {t('customSectionTitle')}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                            <Switch checked={customSectionVisible} onChange={setCustomSectionVisible} />
                        </div>
                    </div>
                    {customSectionVisible && (
                        <>
                            {/* Image Upload Section */}
                            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 16 }}>{t('uploadImageTitle') || 'Görsel Yükle'}</div>
                            <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
                                <Upload
                                    accept=".jpeg,.png,.jpg"
                                    showUploadList={false}
                                    beforeUpload={handleBeforeUpload}
                                >
                                    <Button icon={<UploadOutlined />}>{t('uploadImage')}</Button>
                                    {customSection.imageFile && (
                                        <span style={{ marginLeft: 8 }}>
                                            {customSection.imageFile.name ? customSection.imageFile.name : firmSettings.bot}
                                        </span>
                                    )}
                                </Upload>
                                {firmSettings.botMediaUrl && (
                                    <img
                                        src={customSection.imagePreview || firmSettings.botMediaUrl}
                                        alt="Preview"
                                        style={{ marginTop: 12, maxWidth: 200, maxHeight: 200, borderRadius: 8, border: '1px solid #eee' }}
                                    />
                                )}

                            </div>
                            {/* Description Text Section */}
                            <div style={{ margin: '16px 0 8px 0', fontWeight: 500, fontSize: 16 }}>{t('descriptionTextTitle') || 'Açıklama Metni'}</div>
                            <div style={{ maxWidth: 400 }}>
                                <TextArea
                                    placeholder={t('enterText')}
                                    value={customSection.textValue}
                                    onChange={e => setCustomSection(cs => ({ ...cs, textValue: e.target.value }))}
                                    rows={4}
                                    style={{ borderColor: botTextError ? '#ff4d4f' : undefined }}
                                />
                                {botTextError && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>{botTextError}</div>}
                            </div>

                            {/* Quick Buttons Section */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '18px', marginBottom: 8 }}>
                                <span style={{ fontWeight: 500, fontSize: 16 }}>{t('quickButtonsTitle') || 'Hızlı Butonlar'}</span>
                                <span style={{
                                    background: '#fffbe6',
                                    border: '1px solid #ffe58f',
                                    borderRadius: 6,
                                    color: '#ad6800',
                                    fontSize: 13,
                                    padding: '2px 10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    fontWeight: 500
                                }}>
                                    <span style={{ fontSize: 16 }}>⚠️</span>
                                    {t('maxThreeOptionsInfo') || 'Maksimum 3 seçenek seçebilirsiniz.'}
                                </span>
                            </div>
                            {/* Button Options Grid */}
                            <div style={{ marginTop:"25px",display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    {customOptions.map(opt => {
                                        const isSelected = selectedOptions.find(sel => sel.key === opt.key);
                                        const isDisabled = !isSelected && selectedOptions.length >= 3;
                                        return (
                                            <div
                                                key={opt.key}
                                                onClick={() => !isDisabled && handleSelectOption(opt.key, opt.label)}
                                                style={{
                                                    border: isSelected ? '2px solid #1890ff' : '1px solid #e0e0e0',
                                                    borderRadius: 8,
                                                    padding: 8,
                                                    minWidth: 120,
                                                    minHeight: 10,
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    background: isSelected ? '#e6f7ff' : '#fff',
                                                    boxShadow: isSelected ? '0 0 8px #1890ff44' : 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 500,
                                                    fontSize: 12,
                                                    position: 'relative',
                                                    transition: 'all 0.2s',
                                                    flexDirection: 'column',
                                                    opacity: isDisabled ? 0.5 : 1,
                                                }}
                                            >
                                                <span>{opt.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                            {/* Contact Information Input Fields (shown when phone or website buttons are selected) */}
                            {(selectedOptions.find(sel => sel.key === 4) || selectedOptions.find(sel => sel.key === 5)) && (
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                                    {selectedOptions.find(sel => sel.key === 4) && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <span style={{ fontSize: 13, fontWeight: 500, color: '#333', marginBottom: 2 }}>Telefon Numarası</span>
                                            <Input
                                                style={{ maxWidth: 220 }}
                                                placeholder="Telefon Numarası"
                                                value={customSection.phoneNumber}
                                                maxLength={15}
                                                onChange={e => {
                                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                                    setCustomSection(cs => ({ ...cs, phoneNumber: value }));
                                                }}
                                            />
                                        </div>
                                    )}
                                    {selectedOptions.find(sel => sel.key === 5) && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <span style={{ fontSize: 13, fontWeight: 500, color: '#333', marginBottom: 2 }}>Web Sitesi</span>
                                            <Input
                                                style={{ maxWidth: 220 }}
                                                placeholder="Web Sitesi"
                                                value={customSection.website}
                                                onChange={e => setCustomSection(cs => ({ ...cs, website: e.target.value }))}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <hr style={{ opacity: "50%" }} />

                {/* Menu Configuration Section */}
                <div className="mt-1" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <h3><ShopOutlined /> {t("m")}</h3>
                        <p>{t("mExplanation")}</p>
                    </div>
                    <div className="mt-3">
                        <div className="ml-auto" style={{ width: "fit-content" }}>
                            <Switch
                                className="ml-auto"
                                checked={firmSettings.isMenuSelect === true}
                                size="default"
                                onChange={(value) => {
                                    setfirmSettings({ ...firmSettings, isMenuSelect: value });
                                    if (!value) {
                                        // Menü ekle kapatıldıysa, bot seçeneklerinden Menü Görüntüle'yi kaldır
                                        setSelectedOptions((prev) => prev.filter(opt => opt.key !== 2));
                                    }
                                }}
                            />
                        </div>


                        {firmSettings.isMenuSelect ? (
                            <>
                                {/* Menu Type Selection (PDF or Link) */}
                                <div className="d-flex align-items-center ml-auto mt-2" style={{ width: "fit-content" }}>
                                    <label className="mr-2">{t("chooseMenuType")}: </label>
                                    <Radio.Group
                                        options={[
                                            { label: "PDF", value: 1 },
                                            { label: "Link", value: 2 }
                                        ]}
                                        defaultValue="Link"
                                        buttonStyle="solid"
                                        onChange={(e) => setfirmSettings({ ...firmSettings, menuType: e.target.value })}
                                        value={firmSettings.menuType == null ? 1 : firmSettings.menuType}
                                    />
                                </div>
                                {/* Menu Configuration Input (Link or PDF Upload) */}
                                <div className="d-flex flex-column align-items-end mt-2">
                                    {firmSettings.menuType === 2 ? (
                                        // Link Input
                                        <div style={{ width: 300 }}>
                                            <Input
                                                onChange={(e) => setfirmSettings({ ...firmSettings, menuPath: e.target.value })}
                                                style={{ borderColor: menuError ? '#ff4d4f' : undefined, width: "100%" }}
                                                placeholder="Menü Linkini giriniz"
                                                defaultValue={menuType == 2 ? firmSettings.menuPath : ""}
                                                value={firmSettings.menuPath}
                                            />
                                            {menuError && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>{menuError}</div>}
                                        </div>
                                    ) : (
                                        // PDF Upload
                                        <div className="d-flex flex-column align-items-end">
                                            <Upload maxCount={1} {...handleUpload()} accept={".pdf"}>
                                                <Button style={{ width: "120px", borderColor: menuError ? '#ff4d4f' : undefined }} icon={<UploadOutlined />}>
                                                    {t("upload")}
                                                </Button>
                                            </Upload>
                                            <span className="text-muted mt-1" style={{ fontSize: "12px", textAlign: "right", width: "100%" }}>
                                                {menuType == 1 ? firmSettings.menuPath || "Dosya Seçilmedi" : ""}
                                            </span>
                                            {menuError && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>{menuError}</div>}
                                        </div>
                                    )}
                                </div>



                            </>
                        ) : (<></>)}

                    </div>




                </div>
                <hr style={{ opacity: "50%" }} />


                {/* Save Button Section */}
                <div style={{ width: "100%", justifyContent: "end", display: "flex" }}>
                    <div className="mt-3" style={{ width: "250px" }}>
                        <ReusableButton text={t('save')} variant="campaign" onClick={handleSaveOrUpdate} icon={<SaveOutlined />} />
                    </div>
                </div>


            </Card>
        </div>
    )
}

export default FirmSettings